import { Dispatch, SetStateAction } from "react";

export type Procedure = {
  type: ProcedureType;
  models: Model[];
  views: View[];
};

export type Surgeon = {
  name: string;
  photo_url: string;
  address: string;
};

export type Clinic = {
  clinic_name: string;
  procedures: Procedure[];
  surgeons: Surgeon[];
  logo_url: string;
};

export type TScreenContext = [
  screen: TScreen,
  setScreen: Dispatch<SetStateAction<TScreen>>,
];

export type Theme = {
  themeColor: string;
};

export type Language = [
  language: "en" | "es",
  setLanguage: Dispatch<SetStateAction<"en" | "es">>,
];

export interface TText {
  [key: string]: {
    [language: string]: string;
  };
}

export type Session = {
  id: string;
  accessToken: string;
};

export type BodyScanImage = {
  url: string;
  viewType: ViewType;
};

export type BodyScan = {
  procedureType: ProcedureType;
  images: BodyScanImage[];
};

export type Model = {
  url: string;
  viewType: ViewType;
  procedureType: ProcedureType;
};

export type View = {
  type: ViewType;
};

export type ImageBlobAndViewTypeType = {
  blob: Blob;
  viewType: ViewType;
  imageUrl: string;
};

export enum UploadTypeType {
  UPLOAD = "Upload",
  SCAN = "Scan",
  DASHBOARD = "Dashboard",
  MODEL = "Model",
  CHATBOT = "Chatbot",
}

export enum FormNameType {
  ONBOARDING_TIER1 = "onboarding-tier1",
  ONBOARDING_TIER1_CHAT = "onboarding-tier1-chat",
  ONBOARDING_SCAN = "onboarding-scan",
  ONBOARDING_SCAN_ALL_POSES = "onboarding-scan-all-poses",
  ONBOARDING_ADVERTISE = "onboarding-advertise",
  ONBOARDING_CHATBOT = "onboarding-chatbot",
}

export enum ScanType {
  BODY = "BODY",
  FACE = "FACE",
  TORSO = "TORSO",
}

export enum AreaOfInterestType {
  ARMS = "ARMS",
  LEGS = "LEGS",
  BACK = "BACK",
  BUTT = "BUTT",
  HEAD = "HEAD",
  FACE = "FACE",
  BREAST = "BREAST",
  TORSO = "TORSO",
  VAGINAL = "VAGINAL",
}

export enum ViewType {
  HEAD_RIGHT = "HEAD_RIGHT",
  HEAD_RIGHT_45 = "HEAD_RIGHT_45",
  HEAD_FRONT = "HEAD_FRONT",
  HEAD_LEFT_45 = "HEAD_LEFT_45",
  HEAD_LEFT = "HEAD_LEFT",
  BODY_RIGHT = "BODY_RIGHT",
  BODY_RIGHT_45 = "BODY_RIGHT_45",
  BODY_FRONT = "BODY_FRONT",
  BODY_LEFT_45 = "BODY_LEFT_45",
  BODY_LEFT = "BODY_LEFT",
  BODY_BACK = "BODY_BACK",
  TORSO_RIGHT = "TORSO_RIGHT",
  TORSO_RIGHT_45 = "TORSO_RIGHT_45",
  TORSO_FRONT = "TORSO_FRONT",
  TORSO_LEFT_45 = "TORSO_LEFT_45",
  TORSO_LEFT = "TORSO_LEFT",
  NONE = "",
}

export enum DeviceType {
  DESKTOP = "desktop",
  MOBILE = "mobile",
  TABLET = "tablet",
  OTHER = "other",
  UNKNOWN = "unknown",
}

export enum ProcedureType {
  // currently supported procedures
  BBL = "BBL",
  TUMMY_TUCK = "TUMMY_TUCK",
  BREAST_LIFT = "BREAST_LIFT",
  BREAST_REDUCTION = "BREAST_REDUCTION",
  BREAST_AUGMENTATION = "BREAST_AUGMENTATION",
  BREAST_LIFT_AND_AUGMENTATION = "BREAST_LIFT_AND_AUGMENTATION",
  LIP_FILLER = "LIP_FILLER",
  // currently non-supported procedures
  BURN_REPAIR = "BURN_REPAIR",
  SCAR_REVISION = "SCAR_REVISION",
  HAND_SURGERY = "HAND_SURGERY",
  RECONSTRUCTIVE_SURGERY = "RECONSTRUCTIVE_SURGERY",
  HAIR_TRANSPLANTATION = "HAIR_TRANSPLANTATION",
  BOTOX_INJECTIONS = "BOTOX_INJECTIONS",
  DERMAL_FILLERS = "DERMAL_FILLERS",
  LASER_TREATMENTS = "LASER_TREATMENTS",
  CHEMICAL_PEEL = "CHEMICAL_PEEL",
  MICRODERMABRASION = "MICRODERMABRASION",
  LASER_HAIR_REMOVAL = "LASER_HAIR_REMOVAL",
  THIGH_LIFT = "THIGH_LIFT",
  ARM_LIFT = "ARM_LIFT",
  BUTTOCK_AUGMENTATION = "BUTTOCK_AUGMENTATION",
  BODY_LIFT = "BODY_LIFT",
  LIPOSUCTION = "LIPOSUCTION",
  CHEEK_AUGMENTATION = "CHEEK_AUGMENTATION",
  CHIN_SURGERY = "CHIN_SURGERY",
  FOREHEAD_LIFT = "FOREHEAD_LIFT",
  OTOPLASTY = "OTOPLASTY",
  RHYTIDECTOMY = "RHYTIDECTOMY",
  BLEPHAROPLASTY = "BLEPHAROPLASTY",
  RHINOPLASTY = "RHINOPLASTY",

  RENUVION = "RENUVION",
  ABDOMINAL_ETCHING = "ABDOMINAL_ETCHING",
  THIGH_LIPOSUCTION = "THIGH_LIPOSUCTION",
  ARMS_LIPOSUCTION = "ARMS_LIPOSUCTION",
  CHIN_LIPOSUCTION = "CHIN_LIPOSUCTION",
  LABIAPLASTY = "LABIAPLASTY",
  VAGINAL_TIGHTENING = "VAGINAL_TIGHTENING",
  VAGINAL_PRP = "VAGINAL_PRP",

  HAIR_TRANSPLANT = "HAIR_TRANSPLANT",
  BIOCHETOMY = "BIOCHETOMY",
  FACE_LIFT = "FACE_LIFT",
  CHIN_IMPLANT = "CHIN_IMPLANT",
}

// export enum procedureSpanishMapping

export enum TFormName {
  ONBOARDING_TIER1 = "onboarding-tier1",
}

export enum TDailyAnalyticsCount {
  SITE_VISITS = "site_visits",
  FORM_VISITS = "form_visits",
  FORM_SIMULATIONS = "form_simulations",
  FORM_SUBMISSIONS = "form_submissions",
}

export enum TCTACopy {
  SEE_HOW_YOU_WILL_LOOK_WITH_A_X = "See how you will look with a X",
  SEE_HOW_A_X_WILL_LOOK_ON_YOU_FOR_FREE = "See how a X will look on you for free",
  SHOW_UP_PREPARED_VISUALIZE_YOUR_X_NOW = "Show up prepared. Visualize your X now",
  SIMULATE_YOUR_NEXT_X = "Simulate your next X",
  VISUALIZE_YOUR_X_IN_TWO_MINUTES = "Visualize your X in two minutes",
  VISUALIZE_YOUR_X_IN_TWO_MINUTES_BLUE = "Visualize your X in two minutes blue",
  VISUALIZE_YOUR_X_INTANTLY = "Visualize your X instantly",
  DEFAULT_COPY = "Default copy",
}

export enum TFormVersion {
  V201 = "v2.0.1",
}

export enum TScreen {
  INTRO_SCREEN = "intro screen",
  SELECT_AREA_OF_INTEREST_SCREEN = "select area of interest screen",
  SELECT_PROCEDURE_SCREEN = "select procedure screen",
  FILL_OUT_PATIENT_INFO_SCREEN = "fill out patient info screen",
  SELECT_UPLOAD_TYPE_SCREEN = "select upload type screen",
  PHOTO_INSTRUCTIONS_SCREEN = "photo instructions screen",
  BODY_SCAN_INSTRUCTIONS_SCREEN = "body scan instructions screen",
  BODY_SCAN_SCREEN = "body scan screen",
  PHOTO_UPLOAD_SCREEN = "photo upload screen",
  SELECT_MODEL_SCREEN = "select model screen",
  BEFORE_AFTER_SCREEN = "before after screen",
  SIMULATION_VIEW_SCREEN = "simulation view screen",
  SIMULATION_VIEW_SCREEN_NON_AI_SUPPORT = "simulation view screen non ai support",
  SELECT_VIRTUAL_CONSULTATION_SCREEN = "select virtual consultation screen",
  BOOK_APPOINTMENT_SCREEN = "book appointment screen",
  FINISH_SCREEN = "finish screen",
  PHOTO_UPLOAD_INSTRUCTIONS_SCREEN = "photo upload instruction screen",
  VIDEO_SCREEN = "video screen",
  CHATBOT_SCREEN = "chatbot screen",
  NONE = "",
}

export enum Sound {
  PlaceYourDevice = "https://clinic-os.com/flow-assets%2Fbody-scanner-ai%2Fsound%2Fellie%2F20240516_Place-your-device-on-a-waist-high-table%2C-tilt-it-slightly-forward%2C-and-step-back-until-the-marked-line-turns-green.mp3",
  PerfectNowAdjust = "https://clinic-os.com/flow-assets%2Fbody-scanner-ai%2Fsound%2Fellie%2F20240516_Great!-Now%2C-adjust-yourself-following-the-guidelines.-Keep-your-arms-to-your-side%2C-slightly-away-from-your-body.mp3",
  PerfectStayStill = "https://clinic-os.com/flow-assets%2Fbody-scanner-ai%2Fsound%2Fellie%2F20240516_Perfect.-Stay-still.-I'll-take-your-photo-now.-3.....-2%E2%80%A6..-1....._AND_camera-shutter-6305.mp3",
  BackToTheFrontAnd45ForFace = "https://clinic-os.com/flow-assets%2Fbody-scanner-ai%2Fsound%2Fellie%2F20240516_Return-to-facing-the-camera.-Then%2C-turn-your-head-slightly-to-your-right-at-a-45-degree-angle..mp3",
  ContinueToTurn45Left = "https://clinic-os.com/flow-assets%2Fbody-scanner-ai%2Fsound%2Fellie%2F20240516_Continue-to-turn-45-degrees-to-the-left.mp3",
  ContinueToTurn45Left2 = "https://clinic-os.com/flow-assets%2Fbody-scanner-ai%2Fsound%2Fellie%2F20240516_Continue-to-turn-45-degrees-to-the-left2.mp3",
  ContinueToTurn45Right = "https://clinic-os.com/flow-assets%2Fbody-scanner-ai%2Fsound%2Fellie%2F20240516_Continue-to-turn-45-degrees-to-the-right.mp3",
  ContinueToTurn90Right = "https://clinic-os.com/flow-assets%2Fbody-scanner-ai%2Fsound%2Fellie%2F20240516_Continue-to-turn-90-degrees-to-the-right.mp3",
  NowTurn45Left = "https://clinic-os.com/flow-assets%2Fbody-scanner-ai%2Fsound%2Fellie%2F20240516_Now%2C-turn-45-degrees-to-the-left.mp3",
  NowTurn90Left = "https://clinic-os.com/flow-assets%2Fbody-scanner-ai%2Fsound%2Fellie%2F20240516_Now%2C-turn-90-degrees-to-the-left.mp3",
  LastOneNowTurn90LeftLeftShoulderToCamera = "https://clinic-os.com/flow-assets%2Fbody-scanner-ai%2Fsound%2Fellie%2F20240516_Last-one!-Turn-90-degrees-to-the-left-again.-Your-left-shoulder-should-be-facing-the-camera.mp3",
  NowTurn90LeftBackToCamera = "https://clinic-os.com/flow-assets%2Fbody-scanner-ai%2Fsound%2Fellie%2FGreat!-Now-turn-90-degrees-to-the-left-again-so-that-your-back-is-facing-the-camera.mp3",
  NowTurn180LeftShoulderToCamera = "https://clinic-os.com/flow-assets%2Fbody-scanner-ai%2Fsound%2Fellie%2F20240516_Now%2C-turn-180-degrees-in-the-opposite-direction.-Your-left-shoulder-should-be-facing-the-camera..mp3",
  NowTurn180LeftEarToCamera = "https://clinic-os.com/flow-assets%2Fbody-scanner-ai%2Fsound%2Fellie%2F20240516_Now%2C-turn-your-head-in-the-opposite-direction.-Your-left-ear-should-be-facing-the-camera..mp3",
  NowAdjust = "https://clinic-os.com/flow-assets%2Fbody-scanner-ai%2Fsound%2Fellie%2F20240516_Now-adjust-yourself-following-the-guidelines.mp3",
  WereDone = "https://clinic-os.com/flow-assets%2Fbody-scanner-ai%2Fsound%2Fellie%2F20240516_We're-done!-Review-the-photos-to-ensure-everything-looks-good.-You-can-retake-any-picture-by-selecting-it..mp3",

  // ES translations
  PlaceYourDeviceES = "https://clinic-os.com/flow-assets/body-scanner-ai/sound/spanish/ES_place-your-device-on-a-waist-height-table.mp3",
  PerfectNowAdjustES = "https://clinic-os.com/flow-assets/body-scanner-ai/sound/spanish/ES_perfect-now-adjust.mp3",
  PerfectStayStillES = "https://clinic-os.com/flow-assets/body-scanner-ai/sound/spanish/ES_perfect-stay-still.mp3",
  BackToTheFrontAnd45ForFaceES = "https://clinic-os.com/flow-assets/body-scanner-ai/sound/spanish/ES_back-to-the-front-and-45-for-face.mp3",
  ContinueToTurn45LeftES = "https://clinic-os.com/flow-assets/body-scanner-ai/sound/spanish/ES_continue-to-turn-left-45.mp3",
  ContinueToTurn45Left2ES = "https://clinic-os.com/flow-assets/body-scanner-ai/sound/spanish/ES_continue-to-turn-left-45-2.mp3",
  ContinueToTurn45RightES = "https://clinic-os.com/flow-assets/body-scanner-ai/sound/spanish/ES_continue-to-turn-right-45.mp3",
  ContinueToTurn90RightES = "https://clinic-os.com/flow-assets/body-scanner-ai/sound/spanish/ES_continue-to-turn-right-90.mp3",
  NowTurn45LeftES = "https://clinic-os.com/flow-assets/body-scanner-ai/sound/spanish/ES_now-turn-left-45.mp3",
  NowTurn90LeftES = "https://clinic-os.com/flow-assets/body-scanner-ai/sound/spanish/ES_turn-left-90.mp3",
  LastOneNowTurn90LeftLeftShoulderToCameraES = "https://clinic-os.com/flow-assets/body-scanner-ai/sound/spanish/ES_last-one-turn-left-90-left-shoulder-to-camera.mp3",
  NowTurn90LeftBackToCameraES = "https://clinic-os.com/flow-assets/body-scanner-ai/sound/spanish/ES_now-turn-left-90-back-to-camera.mp3",
  NowTurn180LeftShoulderToCameraES = "https://clinic-os.com/flow-assets/body-scanner-ai/sound/spanish/ES_now-turn-180-left-shoulder-to-camera.mp3",
  NowTurn180LeftEarToCameraES = "https://clinic-os.com/flow-assets/body-scanner-ai/sound/spanish/ES_now-turn-180-left-ear-to-camera.mp3",
  NowAdjustES = "https://clinic-os.com/flow-assets/body-scanner-ai/sound/spanish/ES_now_adjust.mp3",
  WereDoneES = "https://clinic-os.com/flow-assets/body-scanner-ai/sound/spanish/ES_were_done.mp3",
}

export const SOUNDTRANSLATION: { [key: string]: { [key: string]: Sound } } = {
  PlaceYourDevice: {
    en: Sound.PlaceYourDevice,
    es: Sound.PlaceYourDeviceES,
  },
  PerfectNowAdjust: {
    en: Sound.PerfectNowAdjust,
    es: Sound.PerfectNowAdjustES,
  },
  PerfectStayStill: {
    en: Sound.PerfectStayStill,
    es: Sound.PerfectStayStillES,
  },
  BackToTheFrontAnd45ForFace: {
    en: Sound.BackToTheFrontAnd45ForFace,
    es: Sound.BackToTheFrontAnd45ForFaceES,
  },
  ContinueToTurn45Left: {
    en: Sound.ContinueToTurn45Left,
    es: Sound.ContinueToTurn45LeftES,
  },
  ContinueToTurn45Left2: {
    en: Sound.ContinueToTurn45Left2,
    es: Sound.ContinueToTurn45Left2ES,
  },
  ContinueToTurn45Right: {
    en: Sound.ContinueToTurn45Right,
    es: Sound.ContinueToTurn45RightES,
  },
  ContinueToTurn90Right: {
    en: Sound.ContinueToTurn90Right,
    es: Sound.ContinueToTurn90RightES,
  },
  NowTurn45Left: {
    en: Sound.NowTurn45Left,
    es: Sound.NowTurn45LeftES,
  },
  NowTurn90Left: {
    en: Sound.NowTurn90Left,
    es: Sound.NowTurn90LeftES,
  },
  LastOneNowTurn90LeftLeftShoulderToCamera: {
    en: Sound.LastOneNowTurn90LeftLeftShoulderToCamera,
    es: Sound.LastOneNowTurn90LeftLeftShoulderToCameraES,
  },
  NowAdjust: {
    en: Sound.NowAdjust,
    es: Sound.NowAdjustES,
  },
  WereDone: {
    en: Sound.WereDone,
    es: Sound.WereDoneES,
  },
  NowTurn90LeftBackToCamera: {
    en: Sound.NowTurn90LeftBackToCamera,
    es: Sound.NowTurn90LeftBackToCameraES,
  },
  NowTurn180LeftShoulderToCamera: {
    en: Sound.NowTurn180LeftShoulderToCamera,
    es: Sound.NowTurn180LeftShoulderToCameraES,
  },
  NowTurn180LeftEarToCamera: {
    en: Sound.NowTurn180LeftEarToCamera,
    es: Sound.NowTurn180LeftEarToCameraES,
  },
};

export const SoundTextMapping = {
  // Audio generated on https://elevenlabs.io/
  [Sound.PlaceYourDevice]:
    "Place your device on a waist-high table, tilt it slightly forward, and step back until the marked line turns green.",
  [Sound.PerfectNowAdjust]:
    "Great! Now, adjust yourself following the guidelines. Keep your arms to your side, slightly away from your body",
  [Sound.PerfectStayStill]:
    "Perfect. stay still. I'll take your photo now. 3... 2... 1...", // Includes a camera capture sound
  [Sound.BackToTheFrontAnd45ForFace]:
    "Return to facing the camera. Then, turn your head slightly to your right at a 45-degree angle.",
  [Sound.ContinueToTurn45Left]: "Continue to turn 45 degrees to the left.",
  [Sound.ContinueToTurn45Right]: "Continue to turn 45 degrees to the right.",
  [Sound.ContinueToTurn90Right]: "Continue to turn 90 degrees to the right.",
  [Sound.NowTurn45Left]: "Now, turn 45 degrees to the left.",
  [Sound.NowTurn90Left]: "Turn 90 degrees to the left.",
  [Sound.LastOneNowTurn90LeftLeftShoulderToCamera]:
    "Last one! Turn 90 degrees to the left again. Your left shoulder should be facing the camera",
  [Sound.NowAdjust]: "Now adjust yourself following the guidelines.",
  [Sound.WereDone]:
    "We're done! Review the photos to ensure everything looks good. You can retake any picture by selecting it.",
  [Sound.NowTurn90LeftBackToCamera]:
    "Great! Now turn 90 degrees to the left again so that your back is facing the camera.",
  [Sound.NowTurn180LeftShoulderToCamera]:
    "Now, turn 180 degrees in the opposite direction. Your left shoulder should be facing the camera.",
  [Sound.NowTurn180LeftEarToCamera]:
    "Now, turn your head in the opposite direction. Your left ear should be facing the camera.",
  //[Sound.TakeStepToward]: "Take a step toward the camera.",
  //[Sound.TakeStepAway]: "Take a step away from the camera.",
  //[Sound.HigherSurface]: "Place the camera on a higher surface.",

  // ES translations
  [Sound.PlaceYourDeviceES]:
    "Coloque su dispositivo sobre una mesa a la altura de la cintura, inclínelo ligeramente hacia adelante y retroceda hasta que la línea marcada se vuelva verde.",
  [Sound.PerfectNowAdjustES]:
    "¡Excelente! Ahora, ajústate siguiendo las pautas. Mantenga los brazos a los lados, ligeramente alejados de su cuerpo.",
  [Sound.PerfectStayStillES]:
    "Perfecto. quedarse quieto. Te tomaré una foto ahora. 3... 2... 1...", // Includes a camera capture sound
  [Sound.BackToTheFrontAnd45ForFaceES]:
    "Vuelve a mirar hacia la cámara. Luego, gire la cabeza ligeramente hacia la derecha en un ángulo de 45 grados.",
  [Sound.ContinueToTurn45LeftES]: "Continúe girando 45 grados a la izquierda.",
  [Sound.ContinueToTurn45RightES]: "Continúe girando 45 grados a la derecha.",
  [Sound.ContinueToTurn90RightES]: "Continúe girando 90 grados a la derecha.",
  [Sound.NowTurn45LeftES]: "Ahora, gire 45 grados a la izquierda.",
  [Sound.NowTurn90LeftES]: "Gire 90 grados a la izquierda.",
  [Sound.LastOneNowTurn90LeftLeftShoulderToCameraES]:
    "¡El último paso! Gire nuevamente 90 grados a la izquierda. Tu hombro izquierdo debe estar frente a la cámara.",
  [Sound.NowAdjustES]: "Ahora ajústate siguiendo las pautas.",
  [Sound.WereDoneES]:
    "¡Hemos terminado! Revise las fotos para asegurarse de que todo se vea bien. Puede volver a tomar cualquier fotografía seleccionándola.",
  [Sound.NowTurn90LeftBackToCameraES]:
    "¡Excelente! Ahora gire 90 grados hacia la izquierda nuevamente para que su espalda quede frente a la cámara.",
  [Sound.NowTurn180LeftShoulderToCameraES]:
    "Ahora, gira 180 grados en la dirección opuesta. Tu hombro izquierdo debe estar mirando hacia la cámara.",
  [Sound.NowTurn180LeftEarToCameraES]:
    "Ahora, gira 180 grados en la dirección opuesta. Tu oreja izquierda debe estar mirando hacia la cámara.",
};

export enum PostWindowEventName {
  CONTEXT_PROCEDURE_UPDATE = "Context Procedure Update",
  FROM_OPENED = "Form Opened",
  CLOSE_MODAL = "close modal",
  PROCEDURE_UPDATE = "procedure update",
  FLAG_AUTO_FOCUS = "flag auto focus",
}

export enum TMessageSender {
  PATIENT = "PATIENT",
  CLINIC = "CLINIC",
}
